import pick from 'lodash/pick';
import { initiatePrivileged, transitionPrivileged } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { fetchCurrentUserHasOrdersSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { redeemGiftCard } from '../../util/api'; //mz
import { clearData, storeData } from './CheckoutPageSessionHelpers';
import { getProcess } from '../../transactions/transaction';
import {
  hasGiftCardLineItem,
  isPendingPaymentTxState,
  resolveGiftCardTransitionName,
} from './checkoutVoucherTransition';

/** Must match CheckoutPage.js STORAGE_KEY and sessionStorage reads in CheckoutPageSessionHelpers */
export const CHECKOUT_PAGE_STORAGE_KEY = 'CheckoutPage';
import {
  formatMarketplaceApiError,
  getMarketplaceErrorsArray,
  snapshotCheckoutSession,
  logCheckoutFailureLine,
  savePurchaseFailureReport,
  clearPurchaseFailureReport,
  isGiftCardSpeculateError,
} from '../../util/checkoutDiagnostics';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/CheckoutPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/CheckoutPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/CheckoutPage/SPECULATE_TRANSACTION_ERROR';
export const INCREMENT_SPECULATE_RETRY_COUNT = 'app/CheckoutPage/INCREMENT_SPECULATE_RETRY_COUNT';
export const RESET_RETRY_COUNT = 'app/CheckoutPage/RESET_RETRY_COUNT';
export const SET_SPECULATE_409_MANUAL_RETRY_USED =
  'app/CheckoutPage/SET_SPECULATE_409_MANUAL_RETRY_USED';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

export const INITIATE_INQUIRY_REQUEST = 'app/CheckoutPage/INITIATE_INQUIRY_REQUEST';
export const INITIATE_INQUIRY_SUCCESS = 'app/CheckoutPage/INITIATE_INQUIRY_SUCCESS';
export const INITIATE_INQUIRY_ERROR = 'app/CheckoutPage/INITIATE_INQUIRY_ERROR';

export const CUSTOMER_COUPON_REQUEST = 'app/CheckoutPage/CUSTOMER_COUPON_REQUEST ';
export const CUSTOMER_COUPON_SUCCESS = 'app/CheckoutPage/CUSTOMER_COUPON_SUCCESS';
export const CUSTOMER_COUPON_ERROR = 'app/CheckoutPage/CUSTOMER_COUPON_ERROR';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  orderData: null,
  speculateTransactionInProgress: false,
  voucherPricingInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  isClockInSync: false,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  initiateInquiryInProgress: false,
  initiateInquiryError: null,
  giftCardValidationError: null,//mz
  flag: null,
  speculateRetryCount: 0, // Track retry attempts to prevent infinite loops
  speculate409ManualRetryUsed: false,
  speculatedTransactionFetched: false, // Track if speculative transaction has been fetched
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case 'CLEAR_CURRENT_TRANSACTION':
      return {
        ...state,
        transaction: null,
        speculatedTransaction: null,
        pageData: state.pageData ? {
          ...state.pageData,
          transaction: null
        } : null,
        speculateTransactionError: null,
        speculateTransactionInProgress: false,
        voucherPricingInProgress: false,
        speculatedTransactionFetched: false, // Reset to allow retry
      };
    
    case 'RESET_CHECKOUT_STATE':
      return {
        ...initialState,
        listing: state.listing, // Keep listing data
        orderData: state.orderData, // Keep order data
      };

    case SPECULATE_TRANSACTION_REQUEST:
      if (action.meta?.isVoucherPricing) {
        return {
          ...state,
          voucherPricingInProgress: true,
          speculateTransactionError: null,
          giftCardValidationError: null,
        };
      }
      return {
        ...state,
        speculateTransactionInProgress: true,
        voucherPricingInProgress: false,
        speculateTransactionError: null,
        // speculatedTransaction: null,
        giftCardValidationError: null, // mz
        speculateRetryCount: action.meta?.preserveRetryCount ? state.speculateRetryCount : 0,
        speculate409ManualRetryUsed: action.meta?.preserveRetryCount
          ? state.speculate409ManualRetryUsed
          : false,
      };
    case SPECULATE_TRANSACTION_SUCCESS: {
      // Check that the local devices clock is within a minute from the server
      const lastTransitionedAt = payload.transaction?.attributes?.lastTransitionedAt;
      const localTime = new Date();
      const minute = 60000;
      const tx = payload.transaction;
      // *** DERIVE THE FLAG DIRECTLY FROM THE PAYLOAD HERE ***
      const isZeroPrice = tx?.attributes?.payinTotal?.amount === 0;
      return {
        ...state,
        speculateTransactionInProgress: false,
        voucherPricingInProgress: false,
        speculateTransactionError: null,
        speculatedTransaction: payload.transaction,
        isClockInSync: Math.abs(lastTransitionedAt?.getTime() - localTime.getTime()) < minute,
        giftCardValidationError: null, // mz
        flag: isZeroPrice,
        speculatedTransactionFetched: true, // Mark as fetched to prevent repeated calls
      };
    }
    case SPECULATE_TRANSACTION_ERROR: {
    console.error(payload); // eslint-disable-line no-console
    const isGiftCardError = isGiftCardSpeculateError(payload);
    const preserveSpeculatedTransaction =
      isGiftCardError || action.meta?.isVoucherPricing;

    // Check if this is a speculative transaction conflict (which should allow retry after clearing)
    const isSpeculativeConflict = 
      payload.conflictDetails?.code === 'speculative-transaction-conflict' ||
      payload.message?.includes('speculative-transaction-conflict');

    return {
      ...state,
      speculateTransactionInProgress: false,
      voucherPricingInProgress: false,
      speculateTransactionError: payload,
      giftCardValidationError: isGiftCardError ? payload : null,
      speculatedTransaction: preserveSpeculatedTransaction ? state.speculatedTransaction : null,
      // Only mark as fetched for non-speculative conflicts to allow retry after clearing session
      speculatedTransactionFetched: !isSpeculativeConflict,
    };
  }
    case INCREMENT_SPECULATE_RETRY_COUNT:
      return {
        ...state,
        speculateRetryCount: state.speculateRetryCount + 1,
      };
    case SET_SPECULATE_409_MANUAL_RETRY_USED:
      return {
        ...state,
        speculate409ManualRetryUsed: true,
      };
    case 'app/CheckoutPage/RESET_RETRY_COUNT':
      return {
        ...state,
        speculateRetryCount: 0,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    case INITIATE_INQUIRY_REQUEST:
      return { ...state, initiateInquiryInProgress: true, initiateInquiryError: null };
    case INITIATE_INQUIRY_SUCCESS:
      return { ...state, initiateInquiryInProgress: false };
    case INITIATE_INQUIRY_ERROR:
      return { ...state, initiateInquiryInProgress: false, initiateInquiryError: payload };

    case CUSTOMER_COUPON_REQUEST:
      return { ...state };
    case CUSTOMER_COUPON_SUCCESS:
      return { ...state };
    case CUSTOMER_COUPON_ERROR:
      return { ...state};


    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const setInitialValues = (initialValues, saveToSessionStorage = false) => {
  if (saveToSessionStorage && typeof window !== 'undefined') {
    const { listing, orderData } = initialValues;
    storeData(orderData, listing, null, CHECKOUT_PAGE_STORAGE_KEY);
  }
  return {
    type: SET_INITIAL_VALUES,
    payload: pick(initialValues, Object.keys(initialState)),
  };
};

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = order => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = orderId => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = (meta = {}) => ({
  type: SPECULATE_TRANSACTION_REQUEST,
  meta,
});

export const speculateTransactionSuccess = transaction  => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = (e, meta = {}) => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
  meta,
});

export const incrementSpeculateRetryCount = () => ({
  type: INCREMENT_SPECULATE_RETRY_COUNT,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

export const initiateInquiryRequest = () => ({ type: INITIATE_INQUIRY_REQUEST });
export const initiateInquirySuccess = () => ({ type: INITIATE_INQUIRY_SUCCESS });
export const initiateInquiryError = e => ({
  type: INITIATE_INQUIRY_ERROR,
  error: true,
  payload: e,
});

export const customerCouponRequest = () => ({ type: CUSTOMER_COUPON_REQUEST });
export const customerCouponSuccess = () => ({ type: CUSTOMER_COUPON_SUCCESS });
export const customerCouponError = e => ({
  type: CUSTOMER_COUPON_ERROR,
  error: true,
  payload: e,
});
/* ================ Thunks ================ */

export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(initiateOrderRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const { deliveryMethod, quantity, bookingDates, ...otherOrderParams } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};



  // Parameters only for client app's server
  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(quantity ? { quantity } : {}),
  };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };


  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias: processAlias,
        transition: transitionName,
        params: transitionParams,
      };
  const queryParams = {
    include: ['booking', 'provider' , 'stripeAccount', 'user'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    // Clear session storage on successful transaction creation to ensure fresh data
    if (order && order.id && order.id.uuid) {
      clearData('CheckoutPage');
    }
    clearPurchaseFailureReport();

    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    return order;
  };

  const handleError = e => {
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    const apiErr = formatMarketplaceApiError(e);
    const sessionBefore = snapshotCheckoutSession('CheckoutPage');
    const listingIdForLog = orderParams?.listingId?.uuid;

    savePurchaseFailureReport(e, {
      phase: 'initiate-order',
      listingId: listingIdForLog,
      sessionSnapshot: sessionBefore,
    });

    if (e.status === 409) {
      log.breadcrumb('checkout', 'initiateOrder 409 conflict', {
        requestId: apiErr?.requestId,
        listingId: listingIdForLog,
        apiError: apiErr,
        sessionSnapshot: sessionBefore,
      }, 'warning');
      logCheckoutFailureLine('initiate-order-409', apiErr, sessionBefore);
      clearData('CheckoutPage');

      const marketplaceErrors = getMarketplaceErrorsArray(e);
      if (marketplaceErrors.length > 0) {
        const errorData = marketplaceErrors[0];

        const conflictError = {
          ...e,
          message:
            errorData.detail ||
            'A transaction for this listing already exists. Please check your orders or contact support.',
          conflictDetails: {
            code: errorData.code,
            conflictType: errorData.meta?.conflictType,
            listingId: errorData.meta?.listingId,
          },
        };

        const stored = storableError(conflictError);
        dispatch(initiateOrderError(stored));
        log.error(conflictError, 'transaction-conflict-detected', {
          ...transactionIdMaybe,
          listingId: listingIdForLog,
          conflictCode: errorData.code,
          conflictType: errorData.meta?.conflictType,
          marketplaceApiError: apiErr,
          checkoutSessionBeforeClear: sessionBefore,
          ...quantityMaybe,
          ...bookingParamsMaybe,
          ...orderData,
        });
        throw stored;
      }

      const conflictError = {
        ...e,
        message:
          'A transaction for this listing already exists. Please check your orders or try again later.',
        conflictDetails: { status: 409 },
      };

      const storedGeneric = storableError(conflictError);
      dispatch(initiateOrderError(storedGeneric));
      log.error(conflictError, 'transaction-conflict-generic', {
        ...transactionIdMaybe,
        listingId: listingIdForLog,
        marketplaceApiError: apiErr,
        checkoutSessionBeforeClear: sessionBefore,
        ...quantityMaybe,
        ...bookingParamsMaybe,
        ...orderData,
      });
      throw storedGeneric;
    }

    logCheckoutFailureLine('initiate-order-failed', apiErr, sessionBefore);
    const storedFailed = storableError(e);
    dispatch(initiateOrderError(storedFailed));
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: listingIdForLog,
      marketplaceApiError: apiErr,
      checkoutSessionSnapshot: sessionBefore,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    throw storedFailed;
  };
  
  if(transitionName == 'transition/request-payment-no-stripe'){
    return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  }
  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transition(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiate(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

export const confirmPayment = (transactionId, transitionName, transitionParams = {}) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(confirmPaymentRequest());

  const bodyParams = {
    id: transactionId,
    transition: transitionName,
    params: transitionParams,
  };
  const queryParams = {
    include: ['booking', 'provider' , 'stripeAccount', 'user'],
    expand: true,
  };

  return sdk.transactions
    .transition(bodyParams, queryParams)
    .then(response => {
      const order = response.data.data;
      log.navigationDiagnostic('checkout_confirm_payment_success', {
        transactionId: order.id?.uuid,
        transition: transitionName,
      });
      dispatch(confirmPaymentSuccess(order.id));
      return order;
    })
    .catch(e => {
      dispatch(confirmPaymentError(storableError(e)));
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      throw e;
    });
};

export const sendMessage = params => (dispatch, getState, sdk) => {
  const message = params.message;
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

/**
 * Initiate transaction against default-inquiry process
 * Note: At this point inquiry transition is made directly against Marketplace API.
 *       So, client app's server is not involved here unlike with transitions including payments.
 *
 * @param {*} inquiryParams contains listingId and protectedData
 * @param {*} processAlias 'default-inquiry/release-1'
 * @param {*} transitionName 'transition/inquire-without-payment'
 * @returns
 */
export const initiateInquiryWithoutPayment = (inquiryParams, processAlias, transitionName) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateInquiryRequest());

  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(initiateInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: inquiryParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };

  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(response => {
      const transactionId = response.data.data.id;
      dispatch(initiateInquirySuccess());
      return transactionId;
    })
    .catch(e => {
      dispatch(initiateInquiryError(storableError(e)));
      throw e;
    });
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
// In CheckoutPage.duck.js

// In CheckoutPage.duck.js
  export const speculateTransaction = (
    orderParams,
    processAlias,
    transactionId,
    transitionName,
    isPrivilegedTransition,
    options = {},
  ) => (dispatch, getState, sdk) => {
    // Prevent duplicate requests if already in progress
    const state = getState();
    const isVoucherPricing = !!options.isVoucherPricing;
    if (state.CheckoutPage.speculateTransactionInProgress && !isVoucherPricing) {
      return Promise.resolve(); // Return a resolved promise to prevent hanging
    }
    if (state.CheckoutPage.voucherPricingInProgress && isVoucherPricing) {
      return Promise.resolve();
    }

    // Reset retry count for new top-level speculate requests (not auto-retries).
    if (!options.preserveRetryCount) {
      dispatch({ type: 'app/CheckoutPage/RESET_RETRY_COUNT' });
    }

    dispatch(
      speculateTransactionRequest({
        preserveRetryCount: !!options.preserveRetryCount,
        isVoucherPricing,
      })
    );

    const isTransition = !!transactionId;

  const {
    deliveryMethod,
    priceVariantName,
    quantity,
    bookingDates,
    ...otherOrderParams
  } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};
 
  // Parameters only for client app's server
  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(quantity ? { quantity } : {}),
  };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };
  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      }

    const queryParams = {
      include: ['booking', 'provider', 'user'],
      expand: true,
    };
    
    const handleSuccess = response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the speculate response');
      }
              const tx = entities[0];

        // Do not clear CheckoutPage session here. After a full-page return (e.g. Auth0),
        // Redux often has no listing/orderData yet; the checkout effect restores them from
        // sessionStorage. Clearing on speculate success wipes that cache before the effect
        // can re-run (e.g. when currentUser finishes loading), causing a redirect to /purchase.
        clearPurchaseFailureReport();

        dispatch(speculateTransactionSuccess(tx));
        return tx;
    };

      const handleError = e => {
      const giftSpeculateContext = {
        giftCardCodeInRequest: !!transitionParams?.giftCardCode,
      };
      const storableSpeculateError = err =>
        storableError(
          err && typeof err === 'object' ? { ...err, ...giftSpeculateContext } : err
        );
      const dispatchSpeculateError = err =>
        dispatch(
          speculateTransactionError(storableSpeculateError(err), { isVoucherPricing })
        );

      const clearCheckoutSessionForSpeculateRetry = () => {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          ['CheckoutPage', 'pageData', 'transactionRef'].forEach(key => {
            try {
              window.sessionStorage.removeItem(key);
            } catch (err) {
              console.warn('Failed to clear session storage key:', key, err);
            }
          });
        }
        dispatch({ type: 'CLEAR_CURRENT_TRANSACTION' });
      };

      const scheduleSpeculateRetry = (delayMs = 300, retryOrderParams = orderParams) =>
        new Promise((resolve, reject) => {
          setTimeout(() => {
            dispatch(
              speculateTransaction(
                retryOrderParams,
                processAlias,
                null,
                transitionName,
                isPrivilegedTransition,
                { preserveRetryCount: true }
              )
            )
              .then(resolve)
              .catch(reject);
          }, delayMs);
        });

      const apiErrSpec = formatMarketplaceApiError(e);
      const sessionSnapSpec = snapshotCheckoutSession('CheckoutPage');

      savePurchaseFailureReport(e, {
        phase: 'speculate-transaction',
        listingId: transitionParams?.listingId?.uuid,
        sessionSnapshot: sessionSnapSpec,
      });

      try {
        // Recover once if we tried to transition an expired/missing speculative transaction.
        // This can happen when voucher application targets a stale pending-payment tx.
        const statusCode = Number(e?.status || e?.originalError?.status);
        const errorCode =
          e?.error || e?.code || e?.data?.error || e?.data?.errors?.[0]?.code || null;
        const errorMessage = `${e?.message || ''}`.toLowerCase();
        const isExpiredOrMissingTx =
          statusCode === 410 ||
          errorCode === 'transaction-expired' ||
          errorCode === 'transaction-not-found' ||
          errorMessage.includes('transaction has expired');

        if (isExpiredOrMissingTx && transitionParams?.giftCardCode) {
          const currentRetryCount = getState().CheckoutPage.speculateRetryCount || 0;
          const maxRetries = 1;
          if (currentRetryCount >= maxRetries) {
            dispatchSpeculateError(e);
            return e;
          }

          dispatch(incrementSpeculateRetryCount());
          if (typeof window !== 'undefined' && window.sessionStorage) {
            const sessionKeys = ['CheckoutPage', 'pageData', 'transactionRef'];
            sessionKeys.forEach(key => {
              try {
                window.sessionStorage.removeItem(key);
              } catch (err) {
                console.warn('Failed to clear session storage key:', key, err);
              }
            });
          }
          dispatch({ type: 'CLEAR_CURRENT_TRANSACTION' });

          const retryTransitionName =
            processAlias?.startsWith('default-booking') &&
            transitionParams.giftCardCode &&
            !isTransition
              ? 'transition/request-payment'
              : transitionName;

          return new Promise((resolve, reject) => {
            setTimeout(() => {
              dispatch(
                speculateTransaction(
                  transitionParams,
                  processAlias,
                  null,
                  retryTransitionName,
                  isPrivilegedTransition
                )
              )
                .then(resolve)
                .catch(reject);
            }, 300);
          });
        }

        // Clear session storage if this is a transaction conflict to prevent cached mock IDs
        if (e.status === 409) {
          logCheckoutFailureLine('speculate-transaction-409', apiErrSpec, sessionSnapSpec);
          clearData('CheckoutPage');
          
          // Check if we have detailed error information from the server
          if (e.data && e.data.errors && e.data.errors.length > 0) {
            const errorData = e.data.errors[0];
            
            try {
              
              // Create a more user-friendly error message
              const conflictError = {
                ...e,
                message: errorData.detail || 'A transaction for this listing already exists. Please proceed to checkout or check your existing orders.',
                conflictDetails: {
                  code: errorData.code,
                  conflictType: errorData.meta?.conflictType,
                  listingId: errorData.meta?.listingId
                }
              };

              const voucher409DuplicateConflict = 'speculative-duplicate-transaction';
              const isVoucherDuplicate409Retry =
                transitionParams?.giftCardCode &&
                errorData.code === 'speculative-transaction-conflict' &&
                errorData.meta?.conflictType === voucher409DuplicateConflict;

              const isVoucherOnlyFinal409 =
                transitionParams?.giftCardCode &&
                errorData.code === 'speculative-transaction-conflict' &&
                errorData.meta?.conflictType === 'only-final-transactions-for-customer';

              if (isVoucherOnlyFinal409) {
                conflictError.showConflictRecoveryPanel = true;
              } else if (isVoucherDuplicate409Retry) {
                const currentRetryCount = getState().CheckoutPage.speculateRetryCount || 0;
                if (currentRetryCount < 1) {
                  dispatch(incrementSpeculateRetryCount());
                  clearCheckoutSessionForSpeculateRetry();
                  const retryParams = { ...orderParams };
                  delete retryParams.giftCardCode;
                  return scheduleSpeculateRetry(300, retryParams);
                }
                conflictError.showConflictRecoveryPanel = true;
              }
            
              
              // Check if this is a voucher application that failed due to transaction-not-found BEFORE dispatching error
              if (transitionParams?.giftCardCode && errorData.code === 'transaction-not-found') {

                // Check retry count to prevent infinite loops
                const currentRetryCount = getState().CheckoutPage.speculateRetryCount || 0;
                const maxRetries = 2; // Reduce from 3 to 2 to be more conservative

                if (currentRetryCount >= maxRetries) {
                  dispatchSpeculateError({
                    ...conflictError,
                    message: 'Maximum retry attempts reached. Please try again later.',
                  });
                  return conflictError;
                }

                dispatch(incrementSpeculateRetryCount());

                clearCheckoutSessionForSpeculateRetry();

                // Add a longer delay and retry with a new transaction
                return scheduleSpeculateRetry(500);
              }
              
              if (!conflictError.showConflictRecoveryPanel && errorData.code === 'speculative-transaction-conflict') {
                conflictError.showConflictRecoveryPanel = true;
              }

              log.error(conflictError, 'speculative-transaction-conflict-detected', {
                listingId: transitionParams.listingId.uuid,
                conflictCode: errorData.code,
                conflictType: errorData.meta?.conflictType,
                marketplaceApiError: apiErrSpec,
                checkoutSessionBeforeClear: sessionSnapSpec,
                ...quantityMaybe,
                ...bookingParamsMaybe,
                ...orderData,
              });
              dispatchSpeculateError(conflictError);
              // Don't throw the error, just return it to prevent uncaught promise rejection
              return conflictError;
            
            } catch (processingError) {
              console.error('🎫 EXCEPTION during voucher retry processing:', processingError);
              // Fall back to normal error handling
              dispatchSpeculateError(e);
              return e;
            }
          }
          
          // Generic conflict handling for speculative transactions
          const conflictError = {
            ...e,
            message: 'A transaction for this listing already exists. Please proceed to checkout or check your existing orders.',
            conflictDetails: { status: 409 }
          };
          
          log.error(conflictError, 'speculative-transaction-conflict-generic', {
            listingId: transitionParams.listingId.uuid,
            marketplaceApiError: apiErrSpec,
            checkoutSessionBeforeClear: sessionSnapSpec,
            ...quantityMaybe,
            ...bookingParamsMaybe,
            ...orderData,
          });
          dispatchSpeculateError(conflictError);
          // Don't throw the error, just return it to prevent uncaught promise rejection
          return conflictError;
        }
        
        // Handle other errors as before
        logCheckoutFailureLine('speculate-transaction-failed', apiErrSpec, sessionSnapSpec);
        log.error(e, 'speculate-transaction-failed', {
          listingId: transitionParams.listingId.uuid,
          marketplaceApiError: apiErrSpec,
          checkoutSessionSnapshot: sessionSnapSpec,
          ...quantityMaybe,
          ...bookingParamsMaybe,
          ...orderData,
        });
        dispatchSpeculateError(e);
        return e;
      } catch (errorHandlingError) {
        console.error('Error in speculative transaction error handling:', errorHandlingError);
        // Fallback error handling
        const fallbackError = {
          ...e,
          message: 'An unexpected error occurred during price calculation. Please try again or contact support.',
          originalError: e,
          handlingError: errorHandlingError
        };
        dispatchSpeculateError(fallbackError);
        return fallbackError;
      }
    };

 
  
  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};


// export const speculateTransaction = (
//   orderParams,
//   processAlias,
//   transactionId,
//   transitionName,
//   isPrivilegedTransition,
  
// ) => (dispatch, getState, sdk) => {
//   //mz
//   if (!orderParams || !orderParams.listingId) {
//     console.error('Speculate transaction called with invalid orderParams. Aborting.', orderParams);
//     const e = new Error('Could not calculate price. Please refresh the page and try again.');
//     return dispatch(speculateTransactionError(storableError(e)));
//   }
//   dispatch(speculateTransactionRequest());

//   // If we already have a transaction ID, we should transition, not
//   // initiate.
//   const isTransition = !!transactionId;

//   const {
//     deliveryMethod,
//     priceVariantName,
//     quantity,
//     bookingDates,
//     ...otherOrderParams
//   } = orderParams;
//   const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
//   const bookingParamsMaybe = bookingDates || {};

//   // Parameters only for client app's server
//   const orderData = {
//     ...(deliveryMethod ? { deliveryMethod } : {}),
//     ...(priceVariantName ? { priceVariantName } : {}),
//   };

//   // Parameters for Marketplace API
//   const transitionParams = {
//     ...quantityMaybe,
//     ...bookingParamsMaybe,
//     ...otherOrderParams,
//     cardToken: 'CheckoutPage_speculative_card_token',
//   };

//   const bodyParams = isTransition
//     ? {
//         id: transactionId,
//         transition: transitionName,
//         params: transitionParams,
//       }
//     : {
//         processAlias,
//         transition: transitionName,
//         params: transitionParams,
//       };

//   const queryParams = {
//     include: ['booking', 'provider'],
//     expand: true,
//   };

//   const handleSuccess = response => {
//     const entities = denormalisedResponseEntities(response);
//     if (entities.length !== 1) {
//       throw new Error('Expected a resource in the speculate response');
//     }
//     const tx = entities[0];
//     dispatch(speculateTransactionSuccess(tx));
//   };

//   const handleError = e => {
//     log.error(e, 'speculate-transaction-failed', {
//       listingId: transitionParams.listingId.uuid,
//       ...quantityMaybe,
//       ...bookingParamsMaybe,
//       ...orderData,
//     });
//     return dispatch(speculateTransactionError(storableError(e)));
//   };

//   if (isTransition && isPrivilegedTransition) {
//     // transition privileged
//     return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
//       .then(handleSuccess)
//       .catch(handleError);
//   } else if (isTransition) {
//     // transition non-privileged
//     return sdk.transactions
//       .transitionSpeculative(bodyParams, queryParams)
//       .then(handleSuccess)
//       .catch(handleError);
//   } else if (isPrivilegedTransition) {
//     // initiate privileged
//     return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
//       .then(handleSuccess)
//       .catch(handleError);
//   } else {
//     // initiate non-privileged
//     return sdk.transactions
//       .initiateSpeculative(bodyParams, queryParams)
//       .then(handleSuccess)
//       .catch(handleError);
//   }
// };


// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());
  const fetchCurrentUserOptions = {
    callParams: { include: ['stripeCustomer.defaultPaymentMethod'] },
    updateHasListings: false,
    updateNotifications: false,
    enforce: true,
  };

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions))
    .then(response => {
      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};

/**
 * Booking voucher apply: fresh privileged initiate (server picks defer vs no-stripe),
 * then coupon transition only when the tx is still pending-payment without a gift line.
 */
export const applyBookingGiftCardSpeculate =
  ({ giftCardCode, orderParams, processAlias }) =>
  (dispatch, getState) => {
    const processName = processAlias?.split('/')[0];
    const process = processName ? getProcess(processName) : null;
    if (!process) {
      return Promise.resolve(null);
    }

    const requestPayment = process.transitions.REQUEST_PAYMENT;
    const requestPaymentCoupon = process.transitions.REQUEST_PAYMENT_COUPON;

    return dispatch(
      speculateTransaction(
        orderParams,
        processAlias,
        null,
        requestPayment,
        process.isPrivileged(requestPayment),
        { isVoucherPricing: true }
      )
    ).then(tx1 => {
      if (!tx1?.id || tx1.status) {
        return tx1;
      }
      if (hasGiftCardLineItem(tx1.attributes?.lineItems)) {
        return tx1;
      }
      if (isPendingPaymentTxState(tx1.attributes?.state)) {
        return dispatch(
          speculateTransaction(
            orderParams,
            processAlias,
            tx1.id,
            requestPaymentCoupon,
            process.isPrivileged(requestPaymentCoupon),
            { isVoucherPricing: true }
          )
        );
      }
      return tx1;
    });
  };

// Apply gift card/voucher code by triggering speculative transaction
export const createcoup = ({ giftCardCode }) => (dispatch, getState) => {
  if (!giftCardCode) {
    console.warn('Cannot apply voucher: missing giftCardCode');
    return Promise.resolve();
  }

  // Get current page data from both Redux state and session storage
  const state = getState();
  const checkoutState = state.CheckoutPage || {};
  
  // Try to get pageData from multiple sources
  let pageData = checkoutState.pageData;
  
  // If no pageData in state, try to get from session storage
  if (!pageData) {
    try {
      const sessionData = sessionStorage.getItem('CheckoutPage');
      pageData = sessionData ? JSON.parse(sessionData) : null;
    } catch (e) {
      console.warn('Failed to parse session storage data:', e);
    }
  }

  // Extract listing and transaction from current state as fallback
  const listing = checkoutState.listing || pageData?.listing;
  const transaction = checkoutState.transaction || pageData?.transaction;
  
  // Get order data from multiple sources
  let orderData = pageData?.orderData || checkoutState.orderData || {};
  
  // If still empty, try to get from session storage
  if (Object.keys(orderData).length === 0) {
    try {
      const sessionPageData = sessionStorage.getItem('pageData');
      if (sessionPageData) {
        const parsed = JSON.parse(sessionPageData);
        orderData = parsed.orderData || {};
      }
    } catch (e) {
      console.warn('Failed to parse orderData from session storage:', e);
    }
  }

  if (!listing) {
    console.warn('Cannot apply voucher: missing listing data');
    return Promise.resolve();
  }


  // Build order parameters
  const orderParams = {
    ...orderData,
    listingId: listing.id,
    giftCardCode,
  };

  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  const processName = processAlias?.split('/')[0];
  const process = processName ? getProcess(processName) : null;

  if (processAlias?.startsWith('default-booking')) {
    return dispatch(applyBookingGiftCardSpeculate({ giftCardCode, orderParams, processAlias }));
  }

  const existingTransaction = checkoutState.transaction || checkoutState.speculatedTransaction;
  const currentTransition = transaction?.attributes?.lastTransition;
  const currentState =
    existingTransaction?.attributes?.state || transaction?.attributes?.state;

  let transactionId = null;
  let baseTransitionName = 'transition/request-payment';

  if (currentTransition === 'transition/inquire') {
    baseTransitionName = 'transition/request-payment-after-inquiry';
    transactionId = transaction?.id || existingTransaction?.id || null;
  } else {
    const existingTxId = existingTransaction?.id || transaction?.id;
    const hasValidTransactionId =
      existingTxId &&
      ((typeof existingTxId === 'object' && existingTxId.uuid) ||
        typeof existingTxId === 'string');

    if (
      hasValidTransactionId &&
      (existingTransaction || currentState === 'pending-payment' || currentState === 'state/pending-payment')
    ) {
      transactionId = existingTxId;
    } else if (existingTransaction || currentState === 'pending-payment' || currentState === 'state/pending-payment') {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        ['CheckoutPage', 'pageData', 'transactionRef'].forEach(key => {
          try {
            window.sessionStorage.removeItem(key);
          } catch (e) {
            console.warn('Failed to clear session storage key:', key, e);
          }
        });
      }
      transactionId = null;
    }
  }

  const transitionName = resolveGiftCardTransitionName({
    process,
    processAlias,
    baseTransitionName,
    giftCardCode,
    transactionId,
    txState: currentState,
  });

  const isPrivileged = process?.isPrivileged(transitionName) || false;


  // If we're clearing an existing transaction (only when transactionId is null and we couldn't find a valid existing ID), add a small delay and clear Redux state
  if (
    (existingTransaction || currentState === 'pending-payment' || currentState === 'state/pending-payment') &&
    transactionId === null
  ) {
    
    // Clear the current transaction from Redux state
    dispatch({ type: 'CLEAR_CURRENT_TRANSACTION' });
    
    // Add a small delay to ensure cleanup is complete
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(dispatch(speculateTransaction(
          orderParams,
          processAlias,
          transactionId,
          transitionName,
          isPrivileged
        )));
      }, 200);
    });
  }
  
  // If we have an existing transaction ID, proceed immediately with transition
  if (transactionId && existingTransaction) {
  }

  return dispatch(speculateTransaction(
    orderParams,
    processAlias,
    transactionId,
    transitionName,
    isPrivileged
  ));
};

export const retryCheckoutSpeculateAfterConflict =
  ({ orderParams, processAlias, transitionName, isPrivilegedTransition }) =>
  (dispatch, getState) => {
    const { speculate409ManualRetryUsed, speculateTransactionInProgress } =
      getState().CheckoutPage || {};
    if (speculate409ManualRetryUsed || speculateTransactionInProgress) {
      return Promise.resolve();
    }
    dispatch({ type: SET_SPECULATE_409_MANUAL_RETRY_USED });
    if (typeof window !== 'undefined' && window.sessionStorage) {
      ['CheckoutPage', 'pageData', 'transactionRef'].forEach(key => {
        try {
          window.sessionStorage.removeItem(key);
        } catch (err) {
          console.warn('Failed to clear session storage key:', key, err);
        }
      });
    }
    dispatch({ type: 'CLEAR_CURRENT_TRANSACTION' });
    return dispatch(
      speculateTransaction(
        orderParams,
        processAlias,
        null,
        transitionName,
        isPrivilegedTransition,
        { preserveRetryCount: true }
      )
    );
  };


